import React from 'react';
import { Delete } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Touch-friendly numeric PIN pad.
 * - `value`: current PIN string
 * - `onChange(next)`: called with the updated string (clamped to `length`)
 * - `length`: number of digits (default 6)
 * - `onComplete(value)`: called once when the PIN reaches `length` digits
 */
export const PinPad = ({ value = '', onChange, length = 6, onComplete, disabled }) => {
  const press = (digit) => {
    if (disabled || value.length >= length) return;
    const next = (value + digit).slice(0, length);
    onChange(next);
    if (next.length === length && onComplete) onComplete(next);
  };
  const backspace = () => {
    if (disabled || !value.length) return;
    onChange(value.slice(0, -1));
  };

  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

  return (
    <div data-testid="pin-pad">
      {/* Dots */}
      <div className="flex items-center justify-center gap-3 mb-5" data-testid="pin-dots">
        {Array.from({ length }).map((_, i) => (
          <span
            key={i}
            className={cn(
              'h-3.5 w-3.5 rounded-full border-2 transition-colors',
              i < value.length ? 'bg-primary border-primary' : 'border-muted-foreground/40'
            )}
          />
        ))}
      </div>

      {/* Keypad */}
      <div className="grid grid-cols-3 gap-2.5">
        {keys.map((k) => (
          <button
            key={k}
            type="button"
            data-testid={`pin-key-${k}`}
            onClick={() => press(k)}
            disabled={disabled}
            className="h-16 rounded-2xl border border-border bg-card text-2xl font-display font-bold text-foreground shadow-sm-token transition-transform active:scale-[0.96] hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          >
            {k}
          </button>
        ))}
        <span />
        <button
          type="button"
          data-testid="pin-key-0"
          onClick={() => press('0')}
          disabled={disabled}
          className="h-16 rounded-2xl border border-border bg-card text-2xl font-display font-bold text-foreground shadow-sm-token transition-transform active:scale-[0.96] hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        >
          0
        </button>
        <button
          type="button"
          data-testid="pin-key-backspace"
          onClick={backspace}
          disabled={disabled}
          className="h-16 rounded-2xl border border-border bg-muted/40 flex items-center justify-center text-muted-foreground transition-transform active:scale-[0.96] hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          aria-label="backspace"
        >
          <Delete className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
};
